import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, ResponsiveContainer,
  BarChart, Bar, Cell,
} from "recharts";
import { Activity, Plus, Loader2, TrendingUp, ShieldCheck, ShieldAlert, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CPK_COLORS = { Incapaz: "#dc2626", Marginal: "#f59e0b", Capaz: "#16a34a", Excelente: "#059669", "N/A": "#94a3b8" };

function KpiCard({ label, value, sub, color = "#0b1f3a", icon: Icon }) {
  return (
    <div className="bg-white rounded-lg border p-4 shadow-sm">
      <div className="flex items-center gap-2 text-xs text-slate-500 uppercase tracking-wide">
        {Icon && <Icon className="h-3.5 w-3.5" />} {label}
      </div>
      <div className="text-3xl font-black mt-1" style={{ color }}>{value ?? "-"}</div>
      {sub && <div className="text-xs text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

export default function Spc() {
  const [parts, setParts] = useState([]);
  const [partId, setPartId] = useState("");
  const [days, setDays] = useState(30);
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [newValue, setNewValue] = useState("");
  const [newNotes, setNewNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/parts`).then(r => setParts(r.data.filter(p => p.unit_weight > 0)));
  }, []);

  useEffect(() => {
    if (!partId) { setAnalysis(null); return; }
    setLoading(true);
    axios.get(`${API}/spc/analysis?part_id=${partId}&days=${days}`)
      .then(r => setAnalysis(r.data))
      .catch(() => toast.error("Erro a carregar análise"))
      .finally(() => setLoading(false));
  }, [partId, days]);

  const chartData = useMemo(() => {
    if (!analysis) return [];
    return analysis.measurements.map((m, i) => ({
      idx: i + 1,
      value: m.value,
      time: m.timestamp,
      outSpec: (analysis.usl != null && m.value > analysis.usl) || (analysis.lsl != null && m.value < analysis.lsl),
      outCtrl: m.value > analysis.ucl || m.value < analysis.lcl,
    }));
  }, [analysis]);

  const addMeasurement = async () => {
    const v = parseFloat(newValue);
    if (!v || isNaN(v)) { toast.error("Valor inválido"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/spc/measurement`, { part_id: partId, value: v, variable: "weight", notes: newNotes || null });
      toast.success("Medição registada");
      setNewValue(""); setNewNotes(""); setShowAdd(false);
      const r = await axios.get(`${API}/spc/analysis?part_id=${partId}&days=${days}`);
      setAnalysis(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
    finally { setSaving(false); }
  };

  const deleteMeasurement = async (id) => {
    if (!window.confirm("Apagar esta medição?")) return;
    try {
      await axios.delete(`${API}/spc/measurement/${id}`);
      const r = await axios.get(`${API}/spc/analysis?part_id=${partId}&days=${days}`);
      setAnalysis(r.data);
      toast.success("Apagada");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  };

  return (
    <div className="space-y-5" data-testid="spc-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Activity className="h-8 w-8 text-emerald-600" /> SPC — Controlo Estatístico
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Cartas de controlo I-chart, capacidade Cp/Cpk e histograma de distribuição.
          </p>
        </div>
      </div>

      <div className="bg-white border rounded-lg p-4 flex flex-wrap items-end gap-4">
        <div className="flex-1 min-w-[200px]">
          <Label>Peça</Label>
          <Select value={partId} onValueChange={setPartId}>
            <SelectTrigger data-testid="spc-part-select"><SelectValue placeholder="Escolhe uma peça..." /></SelectTrigger>
            <SelectContent>
              {parts.map(p => <SelectItem key={p.id} value={p.id}>{p.code} — {p.description}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Últimos (dias)</Label>
          <Select value={String(days)} onValueChange={(v) => setDays(parseInt(v))}>
            <SelectTrigger className="w-32" data-testid="spc-days"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="1">1 dia</SelectItem>
              <SelectItem value="7">7 dias</SelectItem>
              <SelectItem value="30">30 dias</SelectItem>
              <SelectItem value="90">90 dias</SelectItem>
              <SelectItem value="365">1 ano</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button
          className="bg-emerald-600 hover:bg-emerald-700"
          disabled={!partId}
          onClick={() => setShowAdd(true)}
          data-testid="add-measurement-btn"
        >
          <Plus className="h-4 w-4 mr-1" /> Nova medição
        </Button>
      </div>

      {loading && <div className="flex justify-center p-6"><Loader2 className="h-6 w-6 animate-spin" /></div>}

      {!loading && analysis && (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            <KpiCard label="Cp" value={analysis.cp ?? "-"} sub="≥ 1.33 = capaz" icon={TrendingUp} />
            <KpiCard label="Cpk" value={analysis.cpk ?? "-"}
                     color={CPK_COLORS[analysis.quality_label]}
                     sub={analysis.quality_label}
                     icon={analysis.cpk >= 1.33 ? ShieldCheck : ShieldAlert} />
            <KpiCard label="Média (X̄)" value={analysis.mean} sub={`n = ${analysis.count}`} />
            <KpiCard label="σ (desvio)" value={analysis.sigma} />
            <KpiCard label="Fora spec" value={analysis.out_of_spec_indices.length} color={analysis.out_of_spec_indices.length ? "#dc2626" : "#16a34a"} />
            <KpiCard label="Fora controlo" value={analysis.out_of_control_indices.length} color={analysis.out_of_control_indices.length ? "#dc2626" : "#16a34a"} />
          </div>

          {/* Control Chart */}
          <div className="bg-white border rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-semibold flex items-center gap-2"><Activity className="h-5 w-5 text-emerald-600" /> Carta de Controlo (I-chart)</h2>
              <div className="text-xs text-slate-500">
                USL {analysis.usl ?? "-"} · CL {analysis.mean} · LSL {analysis.lsl ?? "-"}
              </div>
            </div>
            {chartData.length === 0 ? (
              <div className="text-center text-slate-500 py-10">Ainda sem medições para esta peça.</div>
            ) : (
              <ResponsiveContainer width="100%" height={320}>
                <LineChart data={chartData} margin={{ top: 10, right: 20, bottom: 10, left: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                  <XAxis dataKey="idx" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} domain={["auto", "auto"]} />
                  <Tooltip formatter={(v) => v.toFixed ? v.toFixed(3) : v} />
                  {analysis.usl != null && <ReferenceLine y={analysis.usl} stroke="#dc2626" strokeDasharray="3 3" label={{ value: "USL", fontSize: 10, fill: "#dc2626", position: "right" }} />}
                  {analysis.lsl != null && <ReferenceLine y={analysis.lsl} stroke="#dc2626" strokeDasharray="3 3" label={{ value: "LSL", fontSize: 10, fill: "#dc2626", position: "right" }} />}
                  <ReferenceLine y={analysis.ucl} stroke="#f59e0b" strokeDasharray="5 5" label={{ value: "UCL", fontSize: 10, fill: "#f59e0b", position: "right" }} />
                  <ReferenceLine y={analysis.lcl} stroke="#f59e0b" strokeDasharray="5 5" label={{ value: "LCL", fontSize: 10, fill: "#f59e0b", position: "right" }} />
                  <ReferenceLine y={analysis.mean} stroke="#16a34a" label={{ value: "X̄", fontSize: 10, fill: "#16a34a", position: "right" }} />
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="#0b1f3a"
                    strokeWidth={2}
                    // eslint-disable-next-line react/no-unstable-nested-components
                    dot={(props) => {
                      const { cx, cy, payload } = props;
                      const color = payload.outSpec ? "#dc2626" : payload.outCtrl ? "#f59e0b" : "#0b1f3a";
                      return <circle key={`d-${props.index}`} cx={cx} cy={cy} r={4} fill={color} stroke="#fff" strokeWidth={1} />;
                    }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Histogram */}
          {analysis.histogram.length > 0 && (
            <div className="bg-white border rounded-lg p-4">
              <h2 className="font-semibold mb-3">Histograma de distribuição</h2>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={analysis.histogram} margin={{ top: 10, right: 20, bottom: 10, left: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                  <XAxis dataKey="range_start" tickFormatter={(v) => v.toFixed(2)} tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip formatter={(v, name) => name === "count" ? [v, "N.º peças"] : v}
                           labelFormatter={(l, p) => p?.[0]?.payload ? `${p[0].payload.range_start.toFixed(3)} – ${p[0].payload.range_end.toFixed(3)}` : l} />
                  {analysis.usl != null && <ReferenceLine x={analysis.usl} stroke="#dc2626" strokeDasharray="3 3" />}
                  {analysis.lsl != null && <ReferenceLine x={analysis.lsl} stroke="#dc2626" strokeDasharray="3 3" />}
                  <Bar dataKey="count" fill="#0b1f3a">
                    {analysis.histogram.map((b, i) => {
                      const inSpec = (analysis.usl == null || b.range_end <= analysis.usl) && (analysis.lsl == null || b.range_start >= analysis.lsl);
                      return <Cell key={i} fill={inSpec ? "#0b1f3a" : "#dc2626"} />;
                    })}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Table of measurements */}
          <div className="bg-white border rounded-lg overflow-hidden">
            <div className="p-3 border-b font-semibold">Últimas medições ({analysis.measurements.length})</div>
            <div className="max-h-80 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 sticky top-0">
                  <tr>
                    <th className="text-left p-2">#</th>
                    <th className="text-left p-2">Data/hora</th>
                    <th className="text-left p-2">Operador</th>
                    <th className="text-right p-2">Valor</th>
                    <th className="text-left p-2">Notas</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {[...analysis.measurements].reverse().map((m, i) => {
                    const idx = analysis.measurements.length - i;
                    const outSpec = (analysis.usl != null && m.value > analysis.usl) || (analysis.lsl != null && m.value < analysis.lsl);
                    const outCtrl = m.value > analysis.ucl || m.value < analysis.lcl;
                    return (
                      <tr key={m.id} className={`border-t ${outSpec ? "bg-red-50" : outCtrl ? "bg-amber-50" : ""}`} data-testid={`spc-row-${m.id}`}>
                        <td className="p-2 text-slate-500">{idx}</td>
                        <td className="p-2 text-xs">{new Date(m.timestamp).toLocaleString("pt-PT")}</td>
                        <td className="p-2 text-xs">{m.operator_name || "-"}</td>
                        <td className={`p-2 text-right font-mono font-semibold ${outSpec ? "text-red-700" : outCtrl ? "text-amber-700" : ""}`}>
                          {m.value.toFixed(3)}
                        </td>
                        <td className="p-2 text-xs text-slate-600">{m.notes || ""}</td>
                        <td className="p-2 text-right">
                          <button onClick={() => deleteMeasurement(m.id)} className="text-red-500 hover:text-red-700" title="Apagar" data-testid={`spc-del-${m.id}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {!loading && !analysis && partId && (
        <div className="bg-white border rounded-lg p-10 text-center text-slate-500">A carregar...</div>
      )}
      {!partId && (
        <div className="bg-white border rounded-lg p-10 text-center text-slate-500">
          Escolhe uma peça acima para começar a análise SPC.
        </div>
      )}

      {/* Add measurement dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent data-testid="spc-add-dialog">
          <DialogHeader><DialogTitle>Nova medição SPC</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Valor medido (peso em g)</Label>
              <Input type="number" step="0.001" value={newValue} onChange={(e) => setNewValue(e.target.value)} data-testid="spc-value-input" autoFocus />
            </div>
            <div>
              <Label>Notas (opcional)</Label>
              <Input value={newNotes} onChange={(e) => setNewNotes(e.target.value)} placeholder="Ex: cavidade 3, turno da tarde..." data-testid="spc-notes-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowAdd(false)}>Cancelar</Button>
            <Button onClick={addMeasurement} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="spc-save-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Plus className="h-4 w-4 mr-1" />}
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
